import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr, API } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
    UsersThree, Plus, PencilSimple, Paperclip, Eye, Trash,
    ArrowsClockwise, IdentificationCard,
} from "@phosphor-icons/react";

const TIERS = [
    { value: "founder", label: "Founder" },
    { value: "management", label: "Management" },
    { value: "labour", label: "Labour" },
];

const DOC_KINDS = [
    { value: "aadhar", label: "Aadhar" },
    { value: "pan", label: "PAN" },
    { value: "contract", label: "Signed Contract" },
    { value: "other", label: "Other" },
];

export default function Employees() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [tab, setTab] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [emps, usrs] = await Promise.all([
                api.get("/employees", { params: { active_only: !showInactive } }),
                api.get("/users").catch(() => ({ data: [] })),
            ]);
            setItems(emps.data);
            setUsers(usrs.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [showInactive]);
    useEffect(() => { load(); }, [load]);

    // Hierarchy-only rows (self + own manager) are returned by the API for
    // manager-tier users so the picker dropdowns work, but must NEVER show
    // up in the visible table (would leak the founder's salary/bank etc.).
    const visibleItems = useMemo(() => items.filter((e) => !e.is_hierarchy_only), [items]);

    const filtered = useMemo(() => {
        if (tab === "all") return visibleItems;
        return visibleItems.filter((e) => e.tier === tab);
    }, [visibleItems, tab]);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Employees"
                sub="Your team database. Every payroll, advance, and commission calculation looks up records here."
                right={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={load} disabled={loading} data-testid="emp-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}
                                style={{ background: "#f59e0b", color: "#fff" }} data-testid="emp-new-btn">
                            <Plus size={14} className="mr-2" /> New employee
                        </Button>
                    </div>
                }
            />

            <div className="px-8 pt-4 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="flex justify-between items-center">
                    <div className="flex gap-1" data-testid="emp-tabs">
                        {[{k: "all", l: "All"}, ...TIERS.map((t) => ({k: t.value, l: t.label + "s"}))].map((t) => (
                            <button key={t.k} onClick={() => setTab(t.k)}
                                className={`px-4 py-2 text-sm rounded-t-sm border-b-2 ${tab === t.k ? "font-medium" : "text-stone-500"}`}
                                style={{ borderColor: tab === t.k ? "#f59e0b" : "transparent" }}
                                data-testid={`emp-tab-${t.k}`}>
                                {t.l} <span className="text-[10px] text-stone-500">
                                    ({t.k === "all" ? visibleItems.length : visibleItems.filter((e) => e.tier === t.k).length})
                                </span>
                            </button>
                        ))}
                    </div>
                    <label className="text-xs text-stone-500 flex items-center gap-2">
                        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="emp-show-inactive" />
                        Show terminated
                    </label>
                </div>
            </div>

            <div className="p-8">
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="emp-table">
                        <thead>
                            <tr>
                                <th>Code</th><th>Name</th><th>Tier</th><th>Dept · Location</th>
                                <th className="text-right">Base salary</th><th>Manager</th><th>Bank a/c</th><th>Docs</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan="9" className="py-10 text-center text-stone-500">
                                    {loading ? "Loading…" : "No employees yet — click 'New employee' to add your first."}
                                </td></tr>
                            ) : filtered.map((e) => (
                                <tr key={e.id} data-testid={`emp-row-${e.id}`} style={!e.active ? { opacity: 0.5 } : undefined}>
                                    <td className="font-mono text-xs">{e.employee_code}</td>
                                    <td className="font-medium">{e.name}
                                        {!e.active ? <span className="ml-2 se-chip se-chip-err text-[10px]">terminated</span> : null}
                                    </td>
                                    <td><span className="se-chip text-[10px]" style={tierChipStyle(e.tier)}>{e.tier}</span></td>
                                    <td className="text-xs">{e.department || "—"}{e.location ? ` · ${e.location}` : ""}</td>
                                    <td className="se-num text-right">₹ {inr(e.base_salary)}</td>
                                    <td className="text-xs text-stone-500">
                                        {e.manager_id
                                            ? items.find((x) => x.id === e.manager_id)?.name || "—"
                                            : "—"}
                                    </td>
                                    <td className="text-xs text-stone-500 font-mono">{e.bank_account_number || "—"}</td>
                                    <td className="text-xs text-stone-500">{(e.documents || []).length}</td>
                                    <td>
                                        <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setDialogOpen(true); }}
                                                data-testid={`emp-edit-${e.id}`}>
                                            <PencilSimple size={13} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <EmployeeDialog
                open={dialogOpen}
                onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
                editing={editing}
                allEmployees={items}
                allUsers={users}
                onSaved={load}
            />
        </>
    );
}

function tierChipStyle(tier) {
    if (tier === "founder") return { background: "#f5f3ff", color: "#6d28d9" };
    if (tier === "management") return { background: "#eff6ff", color: "#1d4ed8" };
    return { background: "#f0fdf4", color: "#15803d" };
}

// ============================================================================
// New/Edit dialog — big form with tabbed sections
// ============================================================================
function EmployeeDialog({ open, onOpenChange, editing, allEmployees, allUsers, onSaved }) {
    const [form, setForm] = useState(defaultForm());
    const [saving, setSaving] = useState(false);
    const [terminateOpen, setTerminateOpen] = useState(false);
    const [hrMasters, setHrMasters] = useState({ departments: [], locations: [] });

    useEffect(() => {
        if (open) {
            setForm(editing ? {
                ...defaultForm(),
                ...editing,
            } : defaultForm());
            api.get("/settings/hr-masters").then(({ data }) => setHrMasters(data)).catch(() => {});
        }
    }, [open, editing]);

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name?.trim()) return toast.error("Name required");
        if (!form.tier) return toast.error("Pick a tier");
        setSaving(true);
        try {
            const body = { ...form };
            // Coerce number fields
            for (const k of ["base_salary", "pf_employee_amount", "pf_employer_amount", "tds_amount", "tds_slab_pct",
                             "commission_threshold_multiplier", "commission_rate_pct"]) {
                body[k] = Number(body[k]) || 0;
            }
            // Convert empty strings to null for optional fields
            for (const k of Object.keys(body)) {
                if (body[k] === "") body[k] = null;
            }
            if (editing) {
                await api.patch(`/employees/${editing.id}`, body);
                toast.success("Updated");
            } else {
                await api.post("/employees", body);
                toast.success("Employee created");
            }
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const reactivate = async () => {
        try {
            await api.post(`/employees/${editing.id}/reactivate`);
            toast.success("Re-activated");
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // exclude self from manager dropdowns
    const managerOpts = allEmployees.filter((x) => x.id !== editing?.id);

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="emp-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit ${editing.name}` : "New employee"}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6">
                        <Section title="Identity" icon={<IdentificationCard size={16} weight="duotone" />}>
                            <div className="grid grid-cols-3 gap-4">
                                <div><Label>Employee code</Label>
                                    <Input value={form.employee_code || ""} onChange={(e) => setF("employee_code", e.target.value)}
                                           placeholder="auto — EMP-###" data-testid="emp-code" /></div>
                                <div className="col-span-2"><Label>Full name *</Label>
                                    <Input value={form.name || ""} onChange={(e) => setF("name", e.target.value)} data-testid="emp-name" /></div>
                                <div><Label>Tier *</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.tier}
                                            onChange={(e) => setF("tier", e.target.value)} data-testid="emp-tier">
                                        {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select></div>
                                <div><Label>Department</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm bg-white"
                                            value={form.department || ""}
                                            onChange={(e) => setF("department", e.target.value || null)}
                                            data-testid="emp-dept">
                                        <option value="">— Pick a department —</option>
                                        {hrMasters.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                                        {form.department && !hrMasters.departments.includes(form.department) ? (
                                            <option value={form.department}>{form.department} (legacy)</option>
                                        ) : null}
                                    </select>
                                </div>
                                <div><Label>Location</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm bg-white"
                                            value={form.location || ""}
                                            onChange={(e) => setF("location", e.target.value || null)}
                                            data-testid="emp-location">
                                        <option value="">— Pick a location —</option>
                                        {hrMasters.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                                        {form.location && !hrMasters.locations.includes(form.location) ? (
                                            <option value={form.location}>{form.location} (legacy)</option>
                                        ) : null}
                                    </select>
                                </div>
                                <div><Label>Manager</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm"
                                            value={form.manager_id || ""}
                                            onChange={(e) => {
                                                const mid = e.target.value;
                                                // Auto-fill backup manager = manager's own manager (grand-manager),
                                                // but only when adding a new employee AND the user hasn't
                                                // explicitly picked a backup already.
                                                const mgr = (allEmployees || []).find((x) => x.id === mid);
                                                const grandMgr = mgr?.manager_id || "";
                                                setForm((f) => ({
                                                    ...f,
                                                    manager_id: mid,
                                                    // If editing, don't overwrite an existing choice.
                                                    backup_manager_id: editing
                                                        ? f.backup_manager_id
                                                        : (f.backup_manager_id || grandMgr),
                                                }));
                                            }}
                                            data-testid="emp-manager">
                                        <option value="">— None —</option>
                                        {managerOpts.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.employee_code})</option>)}
                                    </select></div>
                                <div><Label>Backup manager</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.backup_manager_id || ""}
                                            onChange={(e) => setF("backup_manager_id", e.target.value)} data-testid="emp-backup-manager">
                                        <option value="">— None —</option>
                                        {managerOpts.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.employee_code})</option>)}
                                    </select></div>
                                <div><Label>Biometric punch code</Label>
                                    <Input value={form.punch_code || ""} onChange={(e) => setF("punch_code", e.target.value)}
                                           placeholder="e.g. CP001, PD0002 — from fingerprint machine"
                                           data-testid="emp-punch-code" /></div>
                                <div><Label>Mode</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.mode}
                                            onChange={(e) => setF("mode", e.target.value)} data-testid="emp-mode">
                                        <option value="payroll">Payroll</option>
                                        <option value="contract">Contract</option>
                                    </select></div>
                                <div><Label>Join date</Label>
                                    <Input type="date" value={form.join_date || ""} onChange={(e) => setF("join_date", e.target.value)}
                                           data-testid="emp-join-date" /></div>
                                <div><Label>Login user (for self-service)</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm"
                                            value={form.user_id || ""}
                                            onChange={(e) => setF("user_id", e.target.value || null)}
                                            data-testid="emp-login-user">
                                        <option value="">— Not linked —</option>
                                        {(allUsers || []).filter((u) =>
                                            !u.employee_id_linked || u.employee_id_linked === editing?.id
                                        ).map((u) => (
                                            <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>
                                        ))}
                                    </select>
                                    <div className="text-[10px] text-stone-500 mt-1">
                                        Links this employee to a login. Required for the employee to see their own payslips, raise petty-cash requests, and apply for leave under their name.
                                    </div>
                                </div>
                            </div>
                        </Section>

                        <Section title="Salary & deductions">
                            <div className="grid grid-cols-4 gap-4">
                                <div><Label>Base salary *</Label>
                                    <Input type="number" value={form.base_salary} onChange={(e) => setF("base_salary", e.target.value)}
                                           data-testid="emp-base-salary" /></div>
                                <div><Label>PF (employee share)</Label>
                                    <Input type="number" value={form.pf_employee_amount} onChange={(e) => setF("pf_employee_amount", e.target.value)}
                                           data-testid="emp-pf-emp" /></div>
                                <div><Label>PF (employer share)</Label>
                                    <Input type="number" value={form.pf_employer_amount} onChange={(e) => setF("pf_employer_amount", e.target.value)}
                                           data-testid="emp-pf-er" /></div>
                                <div><Label>TDS slab (%)</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm bg-white"
                                            value={String(form.tds_slab_pct ?? 0)}
                                            onChange={(e) => setF("tds_slab_pct", Number(e.target.value))}
                                            data-testid="emp-tds">
                                        <option value="0">0% — No TDS (income &lt; ₹3L/yr)</option>
                                        <option value="5">5% — Slab 1 (₹3-7L/yr)</option>
                                        <option value="10">10% — Slab 2 (₹7-10L/yr)</option>
                                        <option value="15">15% — Slab 3 (₹10-12L/yr)</option>
                                        <option value="20">20% — Slab 4 (₹12-15L/yr)</option>
                                        <option value="30">30% — Slab 5 (above ₹15L/yr)</option>
                                    </select>
                                    <div className="text-[10px] text-stone-500 mt-1">
                                        Applied on <b>gross earned each month</b> (base + OT + incentive − penalty). Credits <b>TDS Payable</b>.
                                    </div>
                                </div>
                            </div>
                            <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer">
                                <input type="checkbox" checked={!!form.deduct_pt}
                                       onChange={(e) => setF("deduct_pt", e.target.checked)}
                                       className="mt-0.5"
                                       data-testid="emp-deduct-pt" />
                                <div>
                                    <div>Deduct Professional Tax (PT)</div>
                                    <div className="text-[11px] text-stone-500">
                                        When ON, PT (default ₹300) is auto-deducted whenever this person&apos;s monthly gross crosses the threshold in Settings → Statutory. Money credits <b>PT Payable</b>.
                                    </div>
                                </div>
                            </label>
                            <div className="text-[11px] text-stone-500 mt-2">
                                PF employee share is deducted from salary; employer share is a separate company expense.
                                Both go into PF Payable liability. TDS is deducted and sits in TDS Payable.
                            </div>
                        </Section>

                        <Section title="Sales commission">
                            <label className="flex items-center gap-2 mb-3 text-sm">
                                <input type="checkbox" checked={form.commission_eligible}
                                       onChange={(e) => setF("commission_eligible", e.target.checked)} data-testid="emp-comm-eligible" />
                                Eligible for sales commission
                            </label>
                            {form.commission_eligible ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div><Label>Threshold multiplier (× base)</Label>
                                        <Input type="number" step="0.1" value={form.commission_threshold_multiplier}
                                               onChange={(e) => setF("commission_threshold_multiplier", e.target.value)}
                                               data-testid="emp-comm-mult" /></div>
                                    <div><Label>Commission rate (%)</Label>
                                        <Input type="number" step="0.01" value={form.commission_rate_pct}
                                               onChange={(e) => setF("commission_rate_pct", e.target.value)}
                                               data-testid="emp-comm-rate" /></div>
                                    <div className="col-span-2 text-[11px] text-stone-500 p-2 bg-stone-50 rounded-sm">
                                        Threshold = base × {form.commission_threshold_multiplier || 32} = <b>₹ {inr((Number(form.base_salary) || 0) * (Number(form.commission_threshold_multiplier) || 32))}</b>.
                                        Commission = {form.commission_rate_pct || 1}% × (monthly revenue closed − threshold), only when revenue &gt; threshold.
                                    </div>
                                </div>
                            ) : null}
                        </Section>

                        <Section title="Bank account (for salary payment)">
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Account number</Label>
                                    <Input value={form.bank_account_number || ""} onChange={(e) => setF("bank_account_number", e.target.value)}
                                           data-testid="emp-bank-acc" /></div>
                                <div><Label>IFSC code</Label>
                                    <Input value={form.ifsc_code || ""} onChange={(e) => setF("ifsc_code", e.target.value.toUpperCase())}
                                           data-testid="emp-ifsc" /></div>
                                <div><Label>Account holder name</Label>
                                    <Input value={form.account_holder_name || ""} onChange={(e) => setF("account_holder_name", e.target.value)}
                                           placeholder="If different from employee name" data-testid="emp-holder" /></div>
                                <div><Label>Route salary via another employee (optional)</Label>
                                    <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.route_via_employee_id || ""}
                                            onChange={(e) => setF("route_via_employee_id", e.target.value)} data-testid="emp-route-via">
                                        <option value="">— No, pay to their own account —</option>
                                        {managerOpts.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.employee_code}) · a/c {m.bank_account_number || "—"}</option>)}
                                    </select></div>
                            </div>
                            <div className="text-[11px] text-stone-500 mt-2">
                                Use &ldquo;Route via&rdquo; when this person&apos;s salary is credited to another employee&apos;s bank
                                (e.g. workers paid through a supervisor&apos;s account). Pooled payments auto-match at bank
                                reconciliation time.
                            </div>
                        </Section>

                        <Section title="Emergency contact">
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Name</Label>
                                    <Input value={form.emergency_contact_name || ""} onChange={(e) => setF("emergency_contact_name", e.target.value)}
                                           data-testid="emp-emer-name" /></div>
                                <div><Label>Phone</Label>
                                    <Input value={form.emergency_contact_phone || ""} onChange={(e) => setF("emergency_contact_phone", e.target.value)}
                                           data-testid="emp-emer-phone" /></div>
                            </div>
                        </Section>

                        {editing ? (
                            <DocumentsSection employee={editing} onChanged={onSaved} />
                        ) : (
                            <div className="p-4 border rounded-sm text-[11px] text-stone-500" style={{ borderColor: "var(--se-border)" }}>
                                Save the employee first; you&apos;ll be able to attach Aadhar, PAN and Contract documents after that.
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        {editing && editing.active ? (
                            <Button variant="outline" onClick={() => setTerminateOpen(true)}
                                    style={{ borderColor: "#e11d48", color: "#9f1239" }} data-testid="emp-terminate-btn">
                                Terminate
                            </Button>
                        ) : editing && !editing.active ? (
                            <Button variant="outline" onClick={reactivate} data-testid="emp-reactivate-btn">
                                Reactivate
                            </Button>
                        ) : null}
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="emp-save">
                            {saving ? "Saving…" : (editing ? "Save changes" : "Create employee")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <TerminateDialog open={terminateOpen} onOpenChange={setTerminateOpen} employee={editing}
                onDone={() => { onOpenChange(false); onSaved && onSaved(); }} />
        </>
    );
}

function DocumentsSection({ employee, onChanged }) {
    const [uploading, setUploading] = useState(false);
    const [kind, setKind] = useState("aadhar");
    const [file, setFile] = useState(null);
    const [docs, setDocs] = useState(employee?.documents || []);
    useEffect(() => { setDocs(employee?.documents || []); }, [employee]);

    const upload = async () => {
        if (!file) return toast.error("Pick a file first");
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post(`/employees/${employee.id}/documents?kind=${kind}`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setDocs((d) => [...d, data]);
            toast.success("Uploaded");
            setFile(null);
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const remove = async (entry) => {
        if (!window.confirm(`Delete ${entry.kind} document?`)) return;
        try {
            await api.delete(`/employees/${employee.id}/documents/${entry.id}`);
            setDocs((d) => d.filter((x) => x.id !== entry.id));
            toast.success("Deleted");
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Section title={`Documents (${docs.length})`}>
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                    <Label className="text-[11px]">Kind</Label>
                    <select className="border rounded-sm px-3 py-2 text-sm" value={kind}
                            onChange={(e) => setKind(e.target.value)} data-testid="emp-doc-kind">
                        {DOC_KINDS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-sm text-xs cursor-pointer hover:bg-stone-50"
                       style={{ borderColor: "var(--se-border)" }} data-testid="emp-doc-picker">
                    <Paperclip size={13} /> {file ? file.name : "Choose file"}
                    <input type="file" accept="image/*,application/pdf" className="hidden"
                           onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                <Button size="sm" onClick={upload} disabled={uploading || !file}
                        style={{ background: "#f59e0b", color: "#fff" }} data-testid="emp-doc-upload">
                    {uploading ? "Uploading…" : "Upload"}
                </Button>
            </div>
            {docs.length === 0 ? (
                <div className="text-[11px] text-stone-500">No documents uploaded yet.</div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-[10px] text-stone-500 uppercase">
                        <tr><th className="text-left py-1">Kind</th><th className="text-left">File</th><th></th></tr>
                    </thead>
                    <tbody>
                        {docs.map((d) => (
                            <tr key={d.id} className="border-t" style={{ borderColor: "var(--se-border)" }} data-testid={`emp-doc-${d.id}`}>
                                <td className="py-2 capitalize">{d.kind}</td>
                                <td className="text-xs">{d.original_filename}</td>
                                <td className="text-right">
                                    <a href={`${API}/documents/${d.document_id}/download`} target="_blank" rel="noopener noreferrer"
                                       className="text-[11px] underline text-blue-700 mr-3">
                                        <Eye size={12} className="inline mr-1" /> view
                                    </a>
                                    <button onClick={() => remove(d)} className="text-[11px] text-rose-600" data-testid={`emp-doc-del-${d.id}`}>
                                        <Trash size={12} className="inline" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Section>
    );
}

function TerminateDialog({ open, onOpenChange, employee, onDone }) {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/employees/${employee.id}/terminate`, { termination_date: date, reason });
            toast.success("Terminated");
            onOpenChange(false);
            onDone && onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    if (!employee) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="emp-terminate-dialog">
                <DialogHeader><DialogTitle>Terminate {employee.name}?</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-[11px] text-stone-500">
                        This marks the employee inactive from the chosen date. Their final month&apos;s payroll will
                        auto-prorate. History is preserved for audit.
                    </div>
                    <div><Label>Termination date</Label>
                        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="emp-term-date" /></div>
                    <div><Label>Reason (optional)</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="emp-term-reason" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#e11d48", color: "#fff" }} data-testid="emp-term-confirm">
                        {saving ? "…" : "Terminate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, icon, children }) {
    return (
        <div className="border rounded-sm p-4" style={{ borderColor: "var(--se-border)" }}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
                {icon} {title}
            </div>
            {children}
        </div>
    );
}

function defaultForm() {
    return {
        employee_code: "", name: "", tier: "labour", department: "", location: "",
        manager_id: "", backup_manager_id: "", punch_code: "", join_date: "", mode: "payroll",
        base_salary: 0, pf_employee_amount: 0, pf_employer_amount: 0, tds_amount: 0, tds_slab_pct: 0,
        deduct_pt: false,
        commission_eligible: false, commission_threshold_multiplier: 32, commission_rate_pct: 1,
        bank_account_number: "", ifsc_code: "", account_holder_name: "", route_via_employee_id: "",
        emergency_contact_name: "", emergency_contact_phone: "", notes: "",
    };
}
